import type { Doc } from '../document/types.ts';
/**
 * TR-01 / TR-02 measurement (T2-DESIGN §4.2, DESIGN CD-46, CD-51): ONE pass over the prose paragraphs that scores both
 * lexicons, because the two channels read the same tokens and the tokenisation is most of the cost (G2: both channels
 * together ≤ 0.2 s at 500k words). The first channel to run on a document does the pass and caches the result on the `Doc`;
 * the second reads it.
 *
 * The score of a paragraph is `trScoreParagraph`'s (src/engine/lang/en/tr-grams.ts, the one implementation the trainer's gram
 * dump shares): K_p = Σ_g w_g·log1p(count_p(g)) over the lexicon, E_p = the engine's own words of the paragraph. This file
 * computes it with the same tokens, the same unigram/bigram rule and the same insertion order, so K_p is bit-identical
 * (tested against `trScoreParagraph` on real text), but without allocating a span object per match: counts go into a
 * reusable array indexed by gram id.
 *
 * Evidence. K counts every match; evidence spans are recorded only for STRONG markers, the 300 heaviest grams of each side
 * (ties included: a gram is strong when its weight is at least the 300th largest weight of its list), and at most
 * `MAX_SPANS_PER_PARAGRAPH` (5) per paragraph and side, the occurrences with the largest marginal share of K_p
 * (w·(log1p(n) − log1p(n−1)); ties to the earlier one), in text order. A weight-0.14 unigram like "to" matches in a large
 * share of the words of any English text, so a span on every match would highlight half the page and, at 500k words, hold
 * tens of thousands of objects; the strong cut keeps the evidence list readable and the memory flat. The span weight is the
 * marginal share.
 */
import { BANNED_WORDS } from '../ensemble/wording.ts';
import markerData from '../lang/en/v3/tr-markers.json' with { type: 'json' };
import { makeEvidence } from './framework.ts';
import { fmt, proseParagraphs } from './helpers.ts';
import type { Measurement } from './types.ts';

/** Strong markers per side: the heaviest this many grams (ties included). */
export const STRONG_RANK = 300;
/** Evidence spans kept per paragraph and side. */
export const MAX_SPANS_PER_PARAGRAPH = 5;
/** The evidence feature index of the strong-marker spans (`features[2]` of the definitions). */
export const F_STRONG = 2;

export type TrSide = 'machine' | 'human';

interface Lexicon {
	/** gram → id */
	ids: Map<string, number>;
	grams: string[];
	/** Weight of each gram id on each side (0 when the gram is not in that list). */
	w: Record<TrSide, Float64Array>;
	/** The weight a gram needs on each side to be strong. */
	cut: Record<TrSide, number>;
	/** First token of every lexicon bigram: a bigram is built only when the previous token is in this set. */
	biFirst: Set<string>;
}

function compile(): Lexicon {
	const lists = markerData as unknown as Record<TrSide, Array<[string, number]>>;
	const ids = new Map<string, number>();
	const grams: string[] = [];
	for (const side of ['machine', 'human'] as const)
		for (const [g] of lists[side])
			if (!ids.has(g)) {
				ids.set(g, grams.length);
				grams.push(g);
			}
	const w = {
		machine: new Float64Array(grams.length),
		human: new Float64Array(grams.length),
	};
	const cut = { machine: 0, human: 0 };
	const biFirst = new Set<string>();
	for (const side of ['machine', 'human'] as const) {
		for (const [g, x] of lists[side]) {
			(w[side] as Float64Array)[ids.get(g) as number] = x;
			const sp = g.indexOf(' ');
			if (sp > 0) biFirst.add(g.slice(0, sp));
		}
		const sorted = lists[side].map((e) => e[1]).sort((a, b) => b - a);
		cut[side] = sorted[Math.min(STRONG_RANK, sorted.length) - 1] as number;
	}
	return { ids, grams, w, cut, biFirst };
}

const LEX = compile();
/** The weight of a gram on a side (0 when absent), for tests and tools. */
export const trWeight = (side: TrSide, gram: string): number => {
	const id = LEX.ids.get(gram);
	return id === undefined ? 0 : (LEX.w[side][id] as number);
};
/** The strong-marker weight cut of a side. */
export const trStrongCut = (side: TrSide): number => LEX.cut[side];
/** The lexicon of a side as a gram → weight map (the shape `trScoreParagraph` takes). */
export function trLexicon(side: TrSide): Map<string, number> {
	const out = new Map<string, number>();
	for (const [g, x] of (markerData as unknown as Record<TrSide, Array<[string, number]>>)[side])
		out.set(g, x);
	return out;
}

/* ------------------------------------------------------------------------------------------------------------------ tokens */

// The token grammar of `trTokenise` (tr-grams.ts), scanned by hand instead of by regex: letters, then letters and marks, with
// internal apostrophes (' U+2019 U+2018) each followed by a letter; a bigram only across whitespace alone. An ASCII fast path;
// every other code point is classed once by the same Unicode properties (\p{L}, \p{M}) and cached. Equality with
// `trTokenise` / `trScoreParagraph` is tested on real text and on edge cases (test T-CH-TR-00).
const LETTER = /^\p{L}$/u;
const MARK = /^\p{M}$/u;
const cls = new Map<number, number>(); // 1 letter, 2 mark, 0 neither (non-ASCII code points)
const FOLD = /[’‘]/g;

/** 1 = letter, 2 = mark, 0 = other, for a code point. */
function classOf(cp: number): number {
	if (cp < 128) return (cp | 32) >= 97 && (cp | 32) <= 122 ? 1 : 0;
	let c = cls.get(cp);
	if (c === undefined) {
		const ch = String.fromCodePoint(cp);
		c = LETTER.test(ch) ? 1 : MARK.test(ch) ? 2 : 0;
		cls.set(cp, c);
	}
	return c;
}

/** JS `\s` for one UTF-16 unit. */
function isWs(c: number): boolean {
	return (
		c === 32 ||
		(c >= 9 && c <= 13) ||
		c === 0xa0 ||
		c === 0x1680 ||
		(c >= 0x2000 && c <= 0x200a) ||
		c === 0x2028 ||
		c === 0x2029 ||
		c === 0x202f ||
		c === 0x205f ||
		c === 0x3000 ||
		c === 0xfeff
	);
}

const isApos = (c: number): boolean => c === 0x27 || c === 0x2019 || c === 0x2018;

/**
 * Calls `fn(form, start, end, prevForm, prevStart)` for every token of the paragraph text in order; `prevForm` is the
 * previous token's form when it is separated from this one by whitespace alone (the bigram condition), else undefined.
 */
export function forEachToken(
	text: string,
	fn: (
		form: string,
		start: number,
		end: number,
		prevForm: string | undefined,
		prevStart: number
	) => void
): void {
	const n = text.length;
	let i = 0;
	let prevEnd = -1;
	let prevForm: string | undefined;
	let prevStart = 0;
	while (i < n) {
		let c = text.charCodeAt(i);
		let w = 1;
		let cp = c;
		if (c >= 0xd800 && c <= 0xdbff && i + 1 < n) {
			const d = text.charCodeAt(i + 1);
			if (d >= 0xdc00 && d <= 0xdfff) {
				cp = (c - 0xd800) * 0x400 + (d - 0xdc00) + 0x10000;
				w = 2;
			}
		}
		if (classOf(cp) !== 1) {
			i += w;
			continue;
		}
		const s = i;
		i += w;
		let fold = false;
		let ascii = cp < 128;
		for (;;) {
			// letters and marks
			while (i < n) {
				c = text.charCodeAt(i);
				w = 1;
				cp = c;
				if (c >= 0xd800 && c <= 0xdbff && i + 1 < n) {
					const d = text.charCodeAt(i + 1);
					if (d >= 0xdc00 && d <= 0xdfff) {
						cp = (c - 0xd800) * 0x400 + (d - 0xdc00) + 0x10000;
						w = 2;
					}
				}
				if (classOf(cp) === 0) break;
				if (cp >= 128) ascii = false;
				i += w;
			}
			// an internal apostrophe followed by a letter continues the token
			if (i + 1 < n && isApos(text.charCodeAt(i))) {
				const c1 = text.charCodeAt(i + 1);
				let cp1 = c1;
				if (c1 >= 0xd800 && c1 <= 0xdbff && i + 2 < n) {
					const d = text.charCodeAt(i + 2);
					if (d >= 0xdc00 && d <= 0xdfff) cp1 = (c1 - 0xd800) * 0x400 + (d - 0xdc00) + 0x10000;
				}
				if (classOf(cp1) === 1) {
					if (text.charCodeAt(i) !== 0x27) fold = true;
					ascii = ascii && cp1 < 128;
					i += cp1 > 0xffff ? 3 : 2;
					continue;
				}
			}
			break;
		}
		const raw = text.slice(s, i);
		const form =
			ascii && !fold ? raw.toLowerCase() : (fold ? raw.replace(FOLD, "'") : raw).toLowerCase();
		let adj = false;
		if (prevEnd >= 0 && s > prevEnd) {
			if (s - prevEnd === 1) adj = isWs(text.charCodeAt(prevEnd));
			else {
				adj = true;
				for (let j = prevEnd; j < s; j++)
					if (!isWs(text.charCodeAt(j))) {
						adj = false;
						break;
					}
			}
		}
		fn(form, s, i, adj ? prevForm : undefined, prevStart);
		prevEnd = i;
		prevForm = form;
		prevStart = s;
	}
}

export interface TrSideScan {
	measurement: Measurement;
	/** Σ_p K_p, Σ_p E_p (the channel's K and E). */
	K: number;
	E: number;
}

export interface TrScan {
	machine: TrSideScan;
	human: TrSideScan;
}

const cache = new WeakMap<Doc, TrScan>();

const NOT_BANNED = (g: string): boolean => !BANNED_WORDS.test(g);

/** Scores both lexicons over the prose paragraphs of `doc` (cached per Doc). */
export function trScan(doc: Doc): TrScan {
	const hit = cache.get(doc);
	if (hit) return hit;
	const out = scanDoc(doc);
	cache.set(doc, out);
	return out;
}

function scanDoc(doc: Doc): TrScan {
	const P = doc.paragraphs.count;
	const nG = LEX.grams.length;
	const sides = ['machine', 'human'] as const;
	const k = { machine: new Float64Array(P), human: new Float64Array(P) };
	const e = new Float64Array(P);
	const ev = { machine: makeEvidence(), human: makeEvidence() };
	const wM = LEX.w.machine;
	const wH = LEX.w.human;
	const cutM = LEX.cut.machine;
	const cutH = LEX.cut.human;
	const count = new Int32Array(nG);
	const touched: number[] = [];
	// strong occurrences of the current paragraph: start, end, gram id, occurrence number
	let occ: number[] = [];
	// document level
	const docL = new Float64Array(nG); // Σ_p log1p(count)
	const docCount = new Int32Array(nG); // occurrences
	const matched = { machine: 0, human: 0 };
	const strongSpans = { machine: 0, human: 0 };

	for (const p of proseParagraphs(doc)) {
		const b = doc.blocks[doc.paragraphs.block[p] as number];
		if (!b) continue;
		e[p] = doc.paragraphs.ownWords[p] as number;
		const text = doc.text.slice(b.charStart, b.charEnd);
		touched.length = 0;
		occ = [];
		const hit = (id: number, s: number, en: number): void => {
			const c = ((count[id] as number) + 1) | 0;
			count[id] = c;
			if (c === 1) touched.push(id);
			if ((wM[id] as number) >= cutM || (wH[id] as number) >= cutH) occ.push(s, en, id, c);
		};
		forEachToken(text, (form, s, en, prevForm, prevStart) => {
			// the bigram ending at this token (only built when the previous token can start a lexicon bigram)
			if (prevForm !== undefined && LEX.biFirst.has(prevForm)) {
				const id = LEX.ids.get(`${prevForm} ${form}`);
				if (id !== undefined) hit(id, prevStart, en);
			}
			const id = LEX.ids.get(form);
			if (id !== undefined) hit(id, s, en);
		});
		// K_p in first-occurrence order (bit-identical to trScoreParagraph)
		let km = 0;
		let kh = 0;
		for (const id of touched) {
			const c = count[id] as number;
			const l = Math.log1p(c);
			km += (wM[id] as number) * l;
			kh += (wH[id] as number) * l;
			docL[id] = (docL[id] as number) + l;
			docCount[id] = (docCount[id] as number) + c;
			if ((wM[id] as number) > 0) matched.machine += c;
			if ((wH[id] as number) > 0) matched.human += c;
		}
		k.machine[p] = km;
		k.human[p] = kh;
		for (const id of touched) count[id] = 0;
		if (occ.length) {
			for (const side of sides) {
				const w = LEX.w[side];
				const cut = LEX.cut[side];
				const cand: Array<[number, number, number, number]> = []; // start, end, marginal, order
				for (let o = 0; o < occ.length; o += 4) {
					const id = occ[o + 2] as number;
					const x = w[id] as number;
					if (x < cut) continue;
					const c = occ[o + 3] as number;
					cand.push([
						occ[o] as number,
						occ[o + 1] as number,
						x * (Math.log1p(c) - Math.log1p(c - 1)),
						o,
					]);
				}
				if (cand.length > MAX_SPANS_PER_PARAGRAPH) {
					cand.sort((x, y) => y[2] - x[2] || x[3] - y[3]);
					cand.length = MAX_SPANS_PER_PARAGRAPH;
				}
				cand.sort((x, y) => x[0] - y[0] || x[1] - y[1] || x[3] - y[3]);
				for (const c of cand) ev[side].add(c[0] + b.charStart, c[1] + b.charStart, F_STRONG, c[2]);
				strongSpans[side] += cand.length;
			}
		}
	}

	const result = {} as TrScan;
	for (const side of sides) {
		const w = LEX.w[side];
		let K = 0;
		let E = 0;
		for (let p = 0; p < P; p++) {
			K += k[side][p] as number;
			E += e[p] as number;
		}
		let distinct = 0;
		const contrib: Array<[number, number]> = [];
		for (let id = 0; id < nG; id++) {
			if ((w[id] as number) > 0 && (docCount[id] as number) > 0) {
				distinct++;
				contrib.push([id, (w[id] as number) * (docL[id] as number)]);
			}
		}
		contrib.sort((x, y) => y[1] - x[1] || x[0] - y[0]);
		const rate = E > 0 ? (1000 * K) / E : 0;
		const label = side === 'machine' ? 'machine-leaning' : 'human-leaning';
		const top = contrib
			.filter(([id]) => NOT_BANNED(LEX.grams[id] as string))
			.slice(0, 5)
			.map(([id]) => `“${LEX.grams[id]}” ×${docCount[id]}`);
		const summary = [
			`${distinct} distinct ${label} markers matched · ${matched[side]} occurrences in ${fmt(E, 0)} words`,
		];
		if (top.length) summary.push(`largest contributions: ${top.join(', ')}`);
		summary.push(`${fmt(rate, 1)} weighted marker sum per 1,000 words`);
		result[side] = {
			K,
			E,
			measurement: {
				k: k[side],
				e: side === 'machine' ? e : e.slice(),
				evidence: ev[side],
				features: {
					rate,
					markers: distinct,
					matches: matched[side],
					strong: strongSpans[side],
				},
				summary,
			},
		};
	}
	return result;
}
